import React, { useEffect, useState } from "react";
import { Button, LoadingOverlay } from "@mantine/core";

import ExportLeaveModal from "./ExportLeaveModal";
import LeaveExportsList from "./LeaveExportsList";
import { LeaveExport } from "../utils/types";
import { useToasts } from "../../../providers/notifications/ToastsContext";
import {
  useCreateLeaveExportJobApiService,
  useLeaveExportsApiService,
} from "../service/api/LeaveExportsApiService";

const LeaveExportsView: React.FC = () => {
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>(1);
  const [exports, setExports] = useState<LeaveExport[]>([]);
  const [exportsCount, setExportsCount] = useState<number>(0);
  const [pageLoaderVisible, setPageLoaderVisible] = useState<boolean>(true);
  const [exportModalOpened, setExportModalOpened] = useState<boolean>(false);

  const { showToast } = useToasts();

  enum ExportToastTypes {
    CREATED_LEAVE_JOB = "leave_export_job_created",
    REQUESTED_LEAVE_JOB = "leave_export_job_requested",
    FAILED_FETCHING_JOB = "leave_exports_fetching_failed",
    FAILED_CREATE_JOB_GENERAL = "leave_export_job_failed",
    FAILED_CREATE_JOB_DUPLICATE = "leave_export_job_duplicate_name_failed",
  }

  const {
    error,
    isError,
    isSuccess,
    mutate: createExportJob,
    isLoading: creatingExportJob,
  } = useCreateLeaveExportJobApiService();

  const {
    data: leaveExportsData,
    error: leaveExportsError,
    isLoading: leaveExportsLoading,
    isFetching: leaveExportsFetching,
  } = useLeaveExportsApiService(page);

  // Effect to set the page loader visibility based on the loading state of leave exports
  // NOTE(@tseboho): Ideally, this should be set to true when the data is being loaded for the first time but not used when fetching subsequent pages.
  // That would allow the full screen loader to be shown only when the initial data is being fetched, and a smaller loader to be shown for subsequent fetches.
  useEffect(() => {
    setPageLoaderVisible(leaveExportsLoading || leaveExportsFetching);
  }, [leaveExportsLoading, leaveExportsFetching]);

  useEffect(() => {
    // If leaveExportsData is available and has results, update the state
    if (leaveExportsData && leaveExportsData.results.length > 0) {
      setExportsCount(leaveExportsData.count);
      setPages(leaveExportsData.total_pages);
      setExports(leaveExportsData.results);
    }
  }, [leaveExportsData]);

  // Effect to handle errors when fetching leave exports
  useEffect(() => {
    if (leaveExportsError) {
      showToast({
        strapiId: ExportToastTypes.FAILED_FETCHING_JOB,
      });
    }
  }, [leaveExportsError]);

  // Effect to show a toast when the export job is requested
  useEffect(() => {
    if (creatingExportJob) {
      showToast({ strapiId: ExportToastTypes.REQUESTED_LEAVE_JOB });
    }
  }, [creatingExportJob]);

  // Effect to show a toast when the export job is created successfully
  useEffect(() => {
    if (isSuccess) {
      showToast({ strapiId: ExportToastTypes.CREATED_LEAVE_JOB });
    }
  }, [isSuccess]);

  // Effect to show a toast when there is an error creating the export job
  useEffect(() => {
    if (isError) {
      if (error?.response?.status === 409) {
        showToast({
          strapiId: ExportToastTypes.FAILED_CREATE_JOB_DUPLICATE,
        });
      } else {
        showToast({ strapiId: ExportToastTypes.FAILED_CREATE_JOB_GENERAL });
      }
    }
  }, [isError]);

  // Function to start the export job with a given job name or a default one
  // If no job name is provided, it uses the current timestamp as the job name
  const startExport = (jobName?: string): void => {
    createExportJob({
      jobName: jobName ?? Date.now().toString(),
    });
  };

  const renderTabHeaders = (): JSX.Element => (
    <div className="flex-1 flex flex-row items-center justify-between mb-4">
      <h2 className="text-jem-primary-base font-roboto font-bold text-[18px] leading-[100%] tracking-[0px]">
        Exports
      </h2>
      <Button
        type="button"
        variant="filled"
        color="jem-theme"
        disabled={creatingExportJob}
        onClick={() => setExportModalOpened(true)}
      >
        Create export
      </Button>
    </div>
  );

  return (
    <div className="flex-1">
      <LoadingOverlay visible={pageLoaderVisible} />
      {renderTabHeaders()}
      <LeaveExportsList
        page={page}
        pages={pages}
        exports={exports}
        exportCount={exportsCount}
        setPage={setPage}
      />
      <ExportLeaveModal
        startExport={startExport}
        isOpen={exportModalOpened}
        setOpen={setExportModalOpened}
        lang={{
          title: "Export CSV file",
          export: "Export",
          cancel: "Cancel",
          jobName: "Job Name",
        }}
      />
    </div>
  );
};

export default LeaveExportsView;
